import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  RefreshCw,
  TriangleAlert,
  UserRoundCog,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { encodeFunctionData, type Hex } from 'viem'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState, Page, PageHeader } from '@/components/shell'
import {
  ContractWalletBar,
  sameAddress,
  useContractWallet,
  type ContractChainStatus,
  type ExpectedSigner,
} from '@/features/contracts/wallet-bar'
import { PendingAuditBar } from '@/features/contracts/pending-audit-bar'
import {
  api,
  ApiError,
  type OwnershipContractStatus,
  type OwnershipControlsStatus,
  type OwnershipOperationResult,
} from '@/lib/api'
import {
  contractWalletErrorMessage,
  sendOwnershipOperation,
  waitForContractOperation,
  type OwnershipWrite,
} from '@/lib/contract-wallet'
import { explorerAddressUrl, explorerTxUrl, shortAddress } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/contracts/ownership')({
  component: ContractOwnershipPage,
})

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

function isEvmAddress(value: string): boolean {
  return ADDRESS_RE.test(value.trim())
}

// ── Safe calldata 用的最小 ABI（纯编码，不上链）─────────────────────────
const SAFE_ABI = [
  {
    type: 'function',
    name: 'transferOwnership',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'newOwner', type: 'address' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'acceptOwnership',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    type: 'function',
    name: 'upgradeToAndCall',
    stateMutability: 'payable',
    inputs: [
      { name: 'newImplementation', type: 'address' },
      { name: 'data', type: 'bytes' },
    ],
    outputs: [],
  },
] as const

// ── 待补写审计的交易 ────────────────────────────────────────────────────
//
// 与合约控制页同一套口径：交易一旦发出去就必须先补上审计，绝不能在这之前
// 让人再发一笔。这里的操作是所有权交接，重复发送的代价更大。

type OwnershipAuditPayload =
  | { contract: string; kind: 'transfer'; targetAddress: string; reason: string; txHash: string }
  | { contract: string; kind: 'accept'; reason: string; txHash: string }

const PENDING_STORAGE_KEY = 'contract-ownership-audit-pending'

function readStoredPending(): OwnershipAuditPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PENDING_STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as OwnershipAuditPayload
    const validKind = value.kind === 'transfer' || value.kind === 'accept'
    if (
      !validKind ||
      typeof value.contract !== 'string' ||
      typeof value.reason !== 'string' ||
      !/^0x[0-9a-fA-F]{64}$/.test(value.txHash) ||
      (value.kind === 'transfer' && !isEvmAddress(value.targetAddress))
    ) {
      throw new Error('invalid pending operation')
    }
    return value
  } catch {
    try {
      window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
    } catch {
      // 存储被策略禁用时只能靠内存锁，至少本标签页不会重复发送。
    }
    return null
  }
}

function storePending(pending: OwnershipAuditPayload | null): void {
  try {
    if (pending) window.sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending))
    else window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
  } catch {
    // 存不下也不能解锁：哈希已经在手上了。
  }
}

class SubmittedTransactionError extends Error {
  constructor(
    readonly pending: OwnershipAuditPayload,
    cause: unknown,
  ) {
    super(contractWalletErrorMessage(cause))
  }
}

class AuditPendingError extends Error {
  readonly terminal: boolean
  constructor(
    readonly pending: OwnershipAuditPayload,
    cause: unknown,
  ) {
    super(cause instanceof ApiError ? cause.message : '审计接口暂时不可用。')
    // transaction_failed = 链上确实回滚了，这笔交易不可能再补写成功，解除锁定。
    this.terminal = cause instanceof ApiError && cause.errorCode === 'transaction_failed'
  }
}

// ── 待确认的操作 ────────────────────────────────────────────────────────

type OperationSpec =
  | { kind: 'transfer'; contract: string; label: string; address: string }
  | { kind: 'accept'; contract: string; label: string; address: string; owner: string }

type MutationInput = OwnershipWrite & { contract: string; address: string; reason: string }

function ContractOwnershipPage() {
  const can = useCan()
  const qc = useQueryClient()
  const canManage = can('contract', 'manage')

  const status = useQuery({
    queryKey: ['contract-ownership-status'],
    queryFn: () => api.get<OwnershipControlsStatus>('/contracts/ownership/status'),
    staleTime: 15_000,
    retry: false,
  })
  const { wallet, walletBusy, connect, switchChain, disconnect } = useContractWallet()

  const [operation, setOperation] = useState<OperationSpec | null>(null)
  const [pending, setPending] = useState<OwnershipAuditPayload | null>(() => readStoredPending())
  const [lastResult, setLastResult] = useState<OwnershipOperationResult | null>(null)

  const data = status.data
  const ready = data?.ready ? data : null
  const onChain = ready !== null && wallet.chainId === ready.chainId

  const chainStatus: ContractChainStatus | undefined = data
    ? { ready: data.ready, chainId: data.chainId, manager: null, reason: data.reason }
    : undefined

  // 钱包条的「有签名权限」判定取所有合约 owner / pendingOwner 的并集——
  // 只要连的钱包是其中之一，就不该被标成「无签名权限」；具体能操作哪个合约由每张卡各自门控。
  const expected: ExpectedSigner[] = useMemo(() => {
    if (!ready) return []
    const signers: ExpectedSigner[] = []
    for (const contract of ready.contracts) {
      if (contract.owner) signers.push({ label: `${contract.label} owner`, address: contract.owner })
      if (contract.pendingOwner)
        signers.push({ label: `${contract.label} pending`, address: contract.pendingOwner })
    }
    return signers
  }, [ready])

  const mutation = useMutation({
    mutationFn: async (input: MutationInput) => {
      // 有待补写的交易时，这个按钮只做补写，绝不再碰钱包。
      if (pending) {
        try {
          return await api.post<OwnershipOperationResult>(
            '/contracts/ownership/operations',
            pending,
          )
        } catch (error) {
          throw new AuditPendingError(pending, error)
        }
      }

      if (!wallet.address) throw new Error('请先连接可签名的钱包。')

      const { reason, contract, address, ...write } = input
      const txHash = await sendOwnershipOperation({ ...write, account: wallet.address, contract: address })
      const next: OwnershipAuditPayload =
        write.kind === 'transfer'
          ? { contract, kind: 'transfer', targetAddress: write.targetAddress, reason, txHash }
          : { contract, kind: 'accept', reason, txHash }
      setPending(next)
      storePending(next)

      let receipt: Awaited<ReturnType<typeof waitForContractOperation>>
      try {
        receipt = await waitForContractOperation(txHash)
      } catch (error) {
        throw new SubmittedTransactionError(next, error)
      }
      if (receipt.status !== 'success') {
        setPending(null)
        storePending(null)
        throw new Error('链上交易已回滚。')
      }

      try {
        return await api.post<OwnershipOperationResult>('/contracts/ownership/operations', next)
      } catch (error) {
        throw new AuditPendingError(next, error)
      }
    },
    onSuccess: (result) => {
      setLastResult(result)
      setPending(null)
      storePending(null)
      setOperation(null)
      void qc.invalidateQueries({ queryKey: ['contract-ownership-status'] })
      toast.success('操作已确认并写入审计', {
        description: `交易 ${shortAddress(result.txHash)} · 区块 ${result.blockNumber}`,
      })
    },
    onError: (error) => {
      if (error instanceof SubmittedTransactionError) {
        setPending(error.pending)
        setOperation(null)
        toast.warning('交易已提交，等待链上确认', {
          description: '请勿重复提交。可用原交易重新核验链上状态并补写审计。',
        })
        return
      }
      if (error instanceof AuditPendingError) {
        if (error.terminal) {
          setPending(null)
          storePending(null)
          setOperation(null)
          toast.error('链上交易已回滚', {
            description: '原交易未生效，已解除锁定；核对参数后可以重新提交。',
          })
          return
        }
        setPending(error.pending)
        setOperation(null)
        toast.warning('交易尚未完成审计', {
          description: `${error.message} 可用补写入口重试审计，不会再次发送交易。`,
        })
        return
      }
      toast.error('操作失败', { description: contractWalletErrorMessage(error) })
    },
  })

  // 待审计「出口」:补写(可用真实链上 hash 覆盖存的 safeTxHash)与放弃。
  const retryMutation = useMutation({
    mutationFn: async (overrideHash?: string) => {
      if (!pending) throw new Error('无待补写记录。')
      const payload = overrideHash ? { ...pending, txHash: overrideHash } : pending
      return api.post<OwnershipOperationResult>('/contracts/ownership/operations', payload)
    },
    onSuccess: (result) => {
      setLastResult(result)
      setPending(null)
      storePending(null)
      void qc.invalidateQueries({ queryKey: ['contract-ownership-status'] })
      toast.success('补写审计成功', {
        description: `交易 ${shortAddress(result.txHash)} · 区块 ${result.blockNumber}`,
      })
    },
    onError: (error) => {
      if (error instanceof ApiError && error.errorCode === 'transaction_failed') {
        setPending(null)
        storePending(null)
        toast.error('链上交易已回滚', { description: '原交易未生效，已解除锁定。' })
        return
      }
      toast.warning('补写仍未成功', {
        description: error instanceof ApiError ? error.message : '审计接口暂时不可用。',
      })
    },
  })

  const abandonPending = () => {
    setPending(null)
    storePending(null)
    toast('已放弃该待审计记录', {
      description: '本页已解锁。链上如已成功，此操作将不会留下审计记录。',
    })
  }

  return (
    <Page>
      <PageHeader
        title="合约所有权交接"
        description="Ignix Manager 与四个 Ownable2Step 合约的两步式所有权交接。状态直接读链上，发起 / 接受全部由浏览器钱包签名——后台不接触私钥，只在交易上链后核验并写入审计。多签可用每个操作的 Safe calldata 在 Transaction Builder 里执行。"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={status.isFetching}
            onClick={() => void status.refetch()}
          >
            <RefreshCw className={cn('size-3.5', status.isFetching && 'animate-spin')} />
            刷新状态
          </Button>
        }
      />

      {canManage && (
        <ContractWalletBar
          status={chainStatus}
          isLoading={status.isLoading}
          isError={status.isError}
          error={status.error}
          onRetry={() => void status.refetch()}
          wallet={wallet}
          walletBusy={walletBusy}
          expected={expected}
          onConnect={connect}
          onDisconnect={disconnect}
          onSwitchChain={switchChain}
        />
      )}

      {pending && (
        <PendingAuditBar
          title={pending.kind === 'transfer' ? '发起移交' : '接受所有权'}
          txHash={pending.txHash}
          busy={retryMutation.isPending}
          onRetry={(overrideHash) => retryMutation.mutate(overrideHash)}
          onAbandon={abandonPending}
        />
      )}

      {lastResult && !pending && (
        <a
          href={explorerTxUrl(lastResult.txHash)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-apple bg-green/[0.07] px-3.5 py-2.5 text-[11.5px] text-green hover:bg-green/[0.1]"
        >
          <CheckCircle2 className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            最近成功：{shortAddress(lastResult.txHash)} · 区块 {lastResult.blockNumber}
          </span>
          <ExternalLink className="size-3 shrink-0" />
        </a>
      )}

      <StatusSection
        query={status}
        wallet={wallet}
        onChain={onChain}
        canManage={canManage}
        locked={Boolean(pending)}
        onOperate={setOperation}
      />

      <OperationDialog
        operation={operation}
        busy={mutation.isPending}
        locked={Boolean(pending)}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setOperation(null)
        }}
        onSubmit={(input) => mutation.mutate(input)}
      />
    </Page>
  )
}

function StatusSection({
  query,
  wallet,
  onChain,
  canManage,
  locked,
  onOperate,
}: {
  query: ReturnType<typeof useQuery<OwnershipControlsStatus>>
  wallet: { address: string | null }
  onChain: boolean
  canManage: boolean
  locked: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  if (query.isLoading) {
    return (
      <div className="grid gap-3.5 lg:grid-cols-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-48 rounded-apple" />
        ))}
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <section className="surface p-0">
        <EmptyState
          icon={TriangleAlert}
          title="读不到所有权状态"
          description={
            query.error instanceof ApiError ? query.error.message : '请检查后台 API 与网络连接。'
          }
        />
        <div className="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => void query.refetch()}
          >
            <RefreshCw className="size-3.5" />
            重试
          </Button>
        </div>
      </section>
    )
  }

  const status = query.data

  if (!status.ready) {
    return (
      <section className="surface space-y-2 p-6">
        <div className="section-label">链上状态</div>
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-3 text-[12px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">无法读取所有权状态</p>
            <p className="mt-1 text-mut">{status.reason ?? '链上校验不可用。'}</p>
            <p className="mt-1 text-mut2">
              期望链 <span className="tnum font-mono">{status.chainId}</span>
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      {status.contracts.map((contract) => (
        <ContractCard
          key={contract.key}
          contract={contract}
          wallet={wallet}
          onChain={onChain}
          canManage={canManage}
          locked={locked}
          onOperate={onOperate}
        />
      ))}
    </div>
  )
}

function ContractCard({
  contract,
  wallet,
  onChain,
  canManage,
  locked,
  onOperate,
}: {
  contract: OwnershipContractStatus
  wallet: { address: string | null }
  onChain: boolean
  canManage: boolean
  locked: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  const isOwner = onChain && sameAddress(wallet.address, contract.owner)
  const isPendingOwner = onChain && sameAddress(wallet.address, contract.pendingOwner)
  const isManager = contract.key === 'manager'

  return (
    <section className="surface flex flex-col p-6">
      <div className="flex items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <UserRoundCog className="size-4 text-cyan" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">{contract.label}</h2>
            {!contract.configured && (
              <span className="rounded-full bg-edge/10 px-1.5 py-px text-[10px] text-mut2">
                未配置
              </span>
            )}
          </div>
          {contract.address ? (
            <a
              href={explorerAddressUrl(contract.address)}
              target="_blank"
              rel="noreferrer"
              title={contract.address}
              className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-mut2 transition-colors hover:text-cyan"
            >
              {shortAddress(contract.address)}
              <ExternalLink className="size-2.5 shrink-0 opacity-60" />
            </a>
          ) : (
            <p className="mt-1 text-[11px] text-mut2">未配置合约地址</p>
          )}
        </div>
      </div>

      {contract.configured && contract.reason ? (
        <p className="mt-4 flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3 py-2.5 text-[11.5px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {contract.reason}
        </p>
      ) : contract.configured ? (
        <>
          <div className="mt-5 flex-1 space-y-2 text-[11.5px]">
            <FactRow label="当前 owner" value={contract.owner} />
            <FactRow label="待接受" value={contract.pendingOwner} empty="无" />
          </div>

          {canManage && (
            <>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="pressable h-9 rounded-apple text-[12.5px]"
                  disabled={!isOwner || !contract.address || locked}
                  onClick={() =>
                    contract.address &&
                    onOperate({
                      kind: 'transfer',
                      contract: contract.key,
                      label: contract.label,
                      address: contract.address,
                    })
                  }
                >
                  发起移交
                </Button>
                <Button
                  size="sm"
                  className="pressable h-9 rounded-apple text-[12.5px]"
                  disabled={!isPendingOwner || !contract.pendingOwner || !contract.address || locked}
                  onClick={() =>
                    contract.address &&
                    contract.owner &&
                    onOperate({
                      kind: 'accept',
                      contract: contract.key,
                      label: contract.label,
                      address: contract.address,
                      owner: contract.owner,
                    })
                  }
                >
                  接受所有权
                </Button>
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-mut2">
                {isPendingOwner
                  ? '当前钱包是待接受方，可以执行接受。'
                  : isOwner
                    ? contract.pendingOwner
                      ? '已有一笔待接受的移交；再次发起会覆盖它。接受必须由对方钱包执行。'
                      : '当前钱包是 owner，可以发起移交。'
                    : '当前钱包既不是 owner 也不是待接受方，两个操作都不可用。'}
              </p>
            </>
          )}
        </>
      ) : null}

      {contract.address && (
        <SafeCalldataExport address={contract.address} isManager={isManager} />
      )}
    </section>
  )
}

function FactRow({
  label,
  value,
  empty = '—',
}: {
  label: string
  value: string | null
  empty?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-mut2">{label}</span>
      {value ? (
        <a
          href={explorerAddressUrl(value)}
          target="_blank"
          rel="noreferrer"
          title={value}
          className="min-w-0 truncate font-mono text-txt transition-colors hover:text-cyan"
        >
          {shortAddress(value)}
        </a>
      ) : (
        <span className="text-mut2">{empty}</span>
      )}
    </div>
  )
}

/**
 * Safe Transaction Builder 用的 calldata 导出。
 *
 * 纯前端编码，不碰钱包也不上链。这是「用多签钱包执行合约交接/升级」的主路径：
 * 把目标合约地址与 calldata 粘进 Safe，由多签逐字段核对后执行。
 */
function SafeCalldataExport({ address, isManager }: { address: string; isManager: boolean }) {
  const [newOwner, setNewOwner] = useState('')
  const [implementation, setImplementation] = useState('')

  const transferData = useMemo<Hex | null>(() => {
    if (!isEvmAddress(newOwner)) return null
    if (sameAddress(newOwner.trim(), ZERO_ADDRESS)) return null
    try {
      return encodeFunctionData({
        abi: SAFE_ABI,
        functionName: 'transferOwnership',
        args: [newOwner.trim() as `0x${string}`],
      })
    } catch {
      return null
    }
  }, [newOwner])

  const acceptData = useMemo<Hex>(
    () => encodeFunctionData({ abi: SAFE_ABI, functionName: 'acceptOwnership' }),
    [],
  )

  const upgradeData = useMemo<Hex | null>(() => {
    if (!isManager || !isEvmAddress(implementation)) return null
    try {
      return encodeFunctionData({
        abi: SAFE_ABI,
        functionName: 'upgradeToAndCall',
        args: [implementation.trim() as `0x${string}`, '0x'],
      })
    } catch {
      return null
    }
  }, [implementation, isManager])

  return (
    <details className="group mt-5 border-t border-line/60 pt-4">
      <summary className="flex cursor-pointer list-none items-center justify-between text-[11.5px] text-mut2 hover:text-txt">
        <span className="section-label">多签 Safe calldata</span>
        <span className="text-[10.5px] text-dim group-open:hidden">展开</span>
        <span className="hidden text-[10.5px] text-dim group-open:inline">收起</span>
      </summary>

      <div className="mt-3 space-y-4">
        <CopyRow label="目标合约地址" value={address} mono />

        <div className="space-y-1.5">
          <Label className="text-[11.5px] text-mut">transferOwnership 新 owner 地址</Label>
          <Input
            value={newOwner}
            onChange={(event) => setNewOwner(event.target.value)}
            placeholder="0x…"
            spellCheck={false}
            autoComplete="off"
            className="h-8 rounded-apple-sm font-mono text-[12px]"
          />
          {transferData ? (
            <CopyRow label="transferOwnership calldata" value={transferData} mono />
          ) : (
            <p className="text-[11px] text-mut2">填入有效的新 owner 地址后生成 calldata。</p>
          )}
        </div>

        <CopyRow label="acceptOwnership calldata" value={acceptData} mono />

        {isManager && (
          <div className="space-y-1.5">
            <Label className="text-[11.5px] text-mut">upgradeToAndCall 新实现地址（data 恒为 0x）</Label>
            <Input
              value={implementation}
              onChange={(event) => setImplementation(event.target.value)}
              placeholder="0x…"
              spellCheck={false}
              autoComplete="off"
              className="h-8 rounded-apple-sm font-mono text-[12px]"
            />
            {upgradeData ? (
              <CopyRow label="upgradeToAndCall calldata" value={upgradeData} mono />
            ) : (
              <p className="text-[11px] text-mut2">填入有效的新实现地址后生成 calldata。</p>
            )}
          </div>
        )}
      </div>
    </details>
  )
}

function CopyRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('已复制', { description: label })
    } catch {
      toast.error('复制失败', { description: '请手动选择文本复制。' })
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-mut2">{label}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="pressable size-6 rounded-apple-sm text-mut2 hover:text-cyan"
          aria-label={`复制${label}`}
          onClick={() => void copy()}
        >
          <Copy className="size-3" />
        </Button>
      </div>
      <p
        className={cn(
          'break-all rounded-apple-sm bg-void px-2.5 py-1.5 text-[11px] text-txt',
          mono && 'font-mono',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function OperationDialog({
  operation,
  busy,
  locked,
  onOpenChange,
  onSubmit,
}: {
  operation: OperationSpec | null
  busy: boolean
  locked: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: MutationInput) => void
}) {
  const [reason, setReason] = useState('')
  const [target, setTarget] = useState('')

  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= 10 && trimmedReason.length <= 200
  const needsTarget = operation?.kind === 'transfer'
  const targetValid = !needsTarget || isEvmAddress(target)
  const zeroTarget = needsTarget && sameAddress(target.trim(), ZERO_ADDRESS)
  const canSubmit =
    Boolean(operation) && reasonValid && targetValid && !zeroTarget && !locked

  // 对话框常驻、内容随 operation 挂载；不在关闭时清空，理由和地址会串到下一个操作上。
  useEffect(() => {
    if (!operation) {
      setReason('')
      setTarget('')
    }
  }, [operation])

  const submit = () => {
    if (!operation || !canSubmit) return
    if (operation.kind === 'accept') {
      onSubmit({
        kind: 'accept',
        contract: operation.contract,
        address: operation.address,
        reason: trimmedReason,
      })
      return
    }
    onSubmit({
      kind: 'transfer',
      targetAddress: target.trim(),
      contract: operation.contract,
      address: operation.address,
      reason: trimmedReason,
    })
  }

  const title = !operation
    ? ''
    : operation.kind === 'transfer'
      ? `确认移交「${operation.label}」的所有权？`
      : `确认接受「${operation.label}」的所有权？`

  const danger =
    operation?.kind === 'transfer'
      ? '地址填错不会立刻出事——对方接受之前 owner 不变，你可以再发一次覆盖。但一旦对方接受，控制权就完全转移，无法收回。'
      : '接受之后你成为唯一 owner，原 owner 立即失去全部权限。'

  return (
    <AlertDialog
      open={Boolean(operation)}
      onOpenChange={(open) => {
        if (busy) return
        onOpenChange(open)
      }}
    >
      <AlertDialogContent className="rounded-apple-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[17px] text-txt">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
            确认后浏览器钱包会弹出交易签名。交易上链后，后台会重新解码这笔交易的调用参数，
            对不上就不会写审计。
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3.5">
          {operation && (
            <p className="rounded-apple bg-edge/[0.06] px-3 py-2 text-[11.5px] text-mut2">
              目标合约：
              <span className="ml-1 font-mono text-txt">{shortAddress(operation.address)}</span>
            </p>
          )}

          {needsTarget && (
            <div className="space-y-1.5">
              <Label htmlFor="ownership-target" className="text-[12.5px]">
                新 owner 地址
              </Label>
              <Input
                id="ownership-target"
                value={target}
                placeholder="0x…"
                spellCheck={false}
                autoComplete="off"
                aria-invalid={target.length > 0 && (!targetValid || zeroTarget)}
                className="h-9 rounded-apple font-mono text-[12px]"
                onChange={(event) => setTarget(event.target.value)}
              />
              {target.length > 0 && !targetValid && (
                <p className="text-[11px] text-rose">地址必须是 0x 开头的 40 位十六进制字符。</p>
              )}
              {zeroTarget && (
                <p className="text-[11px] text-rose">不能填零地址——服务端会直接拒绝。</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="ownership-reason" className="text-[12.5px]">
                操作理由
              </Label>
              <span className="text-[10.5px] text-mut2">{reason.length}/200</span>
            </div>
            <Textarea
              id="ownership-reason"
              value={reason}
              maxLength={200}
              placeholder="说明触发原因、工单编号或决策依据（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px]"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
            {reason.length > 0 && !reasonValid && (
              <p className="text-[11px] text-rose">操作理由至少需要 10 个字符。</p>
            )}
          </div>
        </div>

        <p className="flex items-start gap-2 rounded-apple bg-rose/[0.06] px-3 py-2.5 text-[11.5px] leading-relaxed text-rose">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {danger}
        </p>

        {locked && (
          <p className="text-[11.5px] leading-relaxed text-warn">
            上一笔交易还没写入审计，本页暂时不会发起新的链上交易。
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-apple" disabled={busy}>
            返回检查
          </AlertDialogCancel>
          <Button className="pressable rounded-apple" disabled={!canSubmit || busy} onClick={submit}>
            {busy ? '等待钱包或链上确认…' : '在钱包中确认'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
